/**
 * 작성자 : 홍선영
 * 날짜 : 2024.12.06
 * 기능 : (사내)업무관리-근태관리-근태신청
 */

import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { AttendanceD, IApplicationFormType, IAttendance } from 'customTypes';
import { userState } from '../atoms';
import { today } from '../utils/formatDate';
import { isImageValid } from '../utils/isImageValid';
import { isAwaitingApprovalFrom, renderApprovalStatus } from '../utils/renderApprovalStatus';
import List from '../pages/c_work/work5/list';
import Form from '../pages/c_work/work5/form';
import { ContentsContainerRoot } from '../assets/styles/ContentsContainerRoot';
import GetFetchStatus from '../hooks/GetFetchStatus';
import BackButton from './BackButton';
import { getAnnualDayString, getAnnualHourString, getAvailableAnnualHours, hoursToDayString } from '../utils/getAnnualnum';
import { COMCD_A_STATUS, COMCD_ATTENDANCE_GUBUN } from '../_constants';
import { useFetchCommonCodeList } from '../services/useSetCodeListInSelectBoxForm';
import useBackHistory from '../hooks/useBackHistory';

const Root = styled(ContentsContainerRoot)`
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }

  .formTitle {
    padding: 0 0.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    font-weight: 500;
    color: var(--text-primary);
  }
  /* 신청자에게 속한 정보. 제목 행 오른쪽 끝에 한 줄 바로 배치한다 */
  .annualSummary {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    /* 제목 오른쪽 끝으로 민다. 배경은 내용 폭까지만 — 남는 공간을 채우지 않는다 */
    margin-left: auto;
    gap: 0.75rem;
    padding: 0.4375rem 0.75rem;
    border-radius: 0.375rem;
    background-color: var(--tonal);
    font-size: 0.8125rem;
    line-height: 1.4;
    /* 4항목이 폭을 넘겨도 페이지 전체가 가로로 밀리지 않게 자기 안에서만 스크롤한다.
       min-width: 0 이 없으면 자식의 white-space: nowrap 때문에 min-content 아래로
       줄어들지 못해 스크롤이 아니라 헤더 자체가 넘친다 */
    min-width: 0;
    max-width: 100%;
    overflow-x: auto;
    &::-webkit-scrollbar {
      display: none;
    }
    .annualItem {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      white-space: nowrap;
      /* 첫 항목을 뺀 나머지 앞에 얇은 세로 구분선 */
      &:not(:first-child) {
        padding-left: 0.75rem;
        border-left: 1px solid var(--outline);
      }
    }
    .label {
      color: var(--text-secondary);
      &::after {
        content: ':';
      }
    }
    .value {
      color: var(--text-primary);
      font-weight: 600;
    }
    .value.available {
      color: var(--filled-orange);
    }
    /* 신청 가능일수가 음수인 경우. 마이너스 연차 사용을 허용하므로 실제로 발생한다 */
    .value.negative {
      color: var(--filled-red);
    }
    .note {
      color: var(--text-disabled);
      font-weight: 400;
    }
  }
  /* 좁은 화면에서는 제목 행에 4항목이 들어가지 않으므로 제목 아래 줄로 내린다.
     flex-basis: 100% 가 줄바꿈을 강제하고, max-width: fit-content 가 배경이
     빈 오른쪽까지 늘어나는 것을 막는다. 라벨은 숨기지 않는다 — 4항목이면 숫자만 남아 구분이 안 된다 */
  @media (max-width: 1024px) {
    .annualSummary {
      flex-basis: 100%;
      max-width: fit-content;
      margin-left: 0;
      gap: 0.5rem;
      padding: 0.375rem 0.5rem;
      font-size: 0.75rem;
      .annualItem:not(:first-child) {
        padding-left: 0.5rem;
      }
    }
  }
  .inputFormsWrapper {
    border-bottom: 1px solid var(--outline);
    padding: 0.5rem;
    width: 100%;
    flex-grow: 0;
    flex-shrink: 0;
    /* 1024px 이하에서 연차 현황 바가 제목 아래 줄로 내려갈 수 있게 한다 */
    flex-wrap: wrap;
    gap: 0.5rem;
    .table {
      height: fit-content;
    }
  }
`;

const INIT_ROW = {
  wDate: '',
  eDate: '',
  cCd: '',
  caCd: '',
  caStatus: '0',
  status: '0',
  caName: '',
  caDepartment: '',
  emerNum: '',
  address: '',
  caGubun: '',
  sDate: '',
  fDate: '',
  caPdate: '',
  caPtime: '',
  caReason: '',
  caReplacement: '',
  delYn: '',
  writer: '',
  editor: '',
  attendanceD1: [],
  attendanceD2: [],
  creportLine: [],
  cdraftD1: [],
  cdraftD2: [],
  currentUserId: '',
  signImg: '',
  uAnnualnum: '0',
  uRannualnum: '0',
  uPannualnum: '0',
  name: '',
  department: '',
  date: '',
  title: '',
  content: '',
  note: '',
  files: [],
};

// 날짜 포맷 변환 함수
const formatDateWithDay = (dateString: string) => {
  const date = dayjs(dateString);
  return date.format('YYYY-MM-DD (ddd)');
};

// 날짜+시간에서 시간만 추출하여 변환 (2024-11-22 10:30 -> 10:30)
const formatDateTime = (dateTimeString: string) => {
  const timeStr = dateTimeString?.split(' ')[1];
  return timeStr || '';
};

// 날짜+시간에서 날짜만 추출하여 변환 (2024-11-22 10:30 -> 2024-11-22)
const formatDate = (dateTimeString: string) => {
  const dateStr = dateTimeString.split(' ')[0];
  return dateStr || '';
};

const S3_URL = process.env.REACT_APP_AWS_ROOT_URL;
const DIRECTORY_NAME = process.env.REACT_APP_AWS_DIRECTORY_NAME;

const AttendanceForm = ({ approvalType, formType }: { approvalType: 'request' | 'approval'; formType: IApplicationFormType }) => {
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const size = useOutletContext<any>();
  const [viewTable, setViewTable] = useState<boolean>(true);
  const [rowState, setRowState] = useState<IAttendance>(INIT_ROW);
  const [isNewAdd, setIsNewAdd] = useState(true);
  const [tableState, setTableState] = useState<IAttendance[] | null>(null);
  const [initTableState, setInitTableState] = useState<IAttendance[] | null>(null);
  const { data: cUserList } = GetFetchStatus({ path: '/user/company', req: { hCd: userInfo.hCd }, useLoginUserInfo: false });
  const { data: caStatusComCdList } = useFetchCommonCodeList(COMCD_A_STATUS, true);
  const { data: caGubunComCdList } = useFetchCommonCodeList(COMCD_ATTENDANCE_GUBUN, true);
  const dataKey = formType === 'attendance' ? { name: 'caName', department: 'caDepartment' } : { name: 'name', department: 'department' };

  const formatInTuiForm = (aList: any[]) => {
    // 공통코드 데이터가 로딩되지 않은 경우 빈 배열 반환
    if (!caStatusComCdList || !caGubunComCdList) {
      return [];
    }

    const tuiForm = aList.map((el: any) => {
      // 결재 탭에서 내가 지금 처리해야 하는 건인지. 처리를 마친 건은 실제 상태(보류/완료/반려)를 보여준다
      const isAwaitingMyApproval = approvalType === 'approval' && isAwaitingApprovalFrom(userInfo.userId, el.currentUserId, el.attendanceD1);

      return {
        ...el,
        isAwaitingMyApproval,
        ...((approvalType === 'approval' || (approvalType === 'request' && formType !== 'attendance')) && { appType: { name1: el[dataKey.name] || '', name2: el[dataKey.department] || '' } }),
        period: { name1: `${formatDateWithDay(el.sDate)} ~ ${formatDateWithDay(el.fDate)}`, name2: `${formatDateTime(el.sDate)} ~ ${formatDateTime(el.fDate)}` },
        appLine: el.attendanceD1?.map((d1: AttendanceD) => d1.userName).join(', '),
        // 목록의 첨부 표시용. 배열을 그대로 셀 값으로 넘기면 정렬 비교가 애매해지므로 개수(number)로 눕힌다
        fileCount: el.files?.length ?? 0,
        caGubunStatus: {
          name1: caGubunComCdList?.find((cd) => cd[COMCD_ATTENDANCE_GUBUN] === el.caGubun)?.cdName || '미분류',
          name2: isAwaitingMyApproval ? t('결재 대기') : caStatusComCdList?.find((cd) => cd[COMCD_A_STATUS] === el.caStatus)?.cdName || '미분류',
          color1: isAwaitingMyApproval ? 'violet' : renderApprovalStatus(el.caStatus || '0').color,
        },
        lStatus: {
          name2: caStatusComCdList?.find((cd) => cd[COMCD_A_STATUS] === el.status)?.cdName || '미분류',
          color1: renderApprovalStatus(el.status || '0').color,
        },
      };
    });
    return tuiForm;
  };

  // 근태 신청일 때
  const setUserBasicInfo = async () => {
    if (cUserList) {
      const u = cUserList.find((el: any) => el.userId === userInfo.userId);
      const isValidSignImg = await isImageValid(`${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${userInfo.userId}.png`);
      const randomString = Math.random().toString(36).substring(7); // 랜덤 문자열을 쿼리스트링으로 추가해서 이미지 캐싱 방지

      if (u) {
        // 로그인한 유저 기본정보
        setRowState({
          ...INIT_ROW,
          [dataKey.name]: u.userName,
          [dataKey.department]: u.partName,
          emerNum: u.emerNum || '',
          address: `${u.address1} ${u.address2}`,
          signImg: isValidSignImg ? `${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${userInfo.userId}.png?v=${randomString}` : '',
          wDate: today(),
          uAnnualnum: u.uAnnualnum,
          uRannualnum: u.uRannualnum,
          date: today(),
        });
      }
    }
  };

  useEffect(() => {
    if (approvalType === 'request') {
      setRowState(INIT_ROW);
      setIsNewAdd(true);
      setUserBasicInfo();
    } else {
      setRowState(INIT_ROW);
      setIsNewAdd(true);
    }
  }, [cUserList, approvalType, formType]);

  useEffect(() => {
    if (!isNewAdd) setViewTable(false);
  }, [isNewAdd]);

  const addBtnAction = () => {
    setIsNewAdd(true);
    setUserBasicInfo();
    if (size.innerSize.W <= 1024) setViewTable(false);
  };

  const backToMain = () => {
    setIsNewAdd(true);
    setViewTable(true);
  };

  const { onBackPrev } = useBackHistory({ isOpen: !viewTable, onPrev: backToMain, pageName: 'attendanceForm' });

  /**
   * 연차 현황 3요소(총/사용/결재대기)는 반드시 같은 응답에서 뽑는다.
   * 소스가 갈리면 다른 계정이 최종 결재한 직후 총·사용(/user/company 캐시)과
   * 결재대기(/attendance)의 시점이 어긋나 4개 숫자의 산수가 맞지 않는다.
   * - 결재 탭: 선택한 문서 = 상신자 기준 값이 rowState에 그대로 들어있다.
   * - 신청 탭: 목록의 모든 행이 본인 문서라 어느 행을 봐도 같다. 문서가 0건이면 대기는 정의상 0이고
   *   그때만 /user/company에서 온 rowState의 총·사용을 쓴다.
   */
  const annualSource = approvalType === 'approval' ? rowState : initTableState?.[0] ?? { ...rowState, uPannualnum: '0' };
  const availableHours = getAvailableAnnualHours(annualSource.uAnnualnum, annualSource.uRannualnum, annualSource.uPannualnum);

  // 결재 탭은 문서 선택 전 rowState가 INIT_ROW라 의미 없는 0일이 노출되므로 caCd로 막고,
  // 신청 탭은 목록 응답이 도착하기 전 대기가 0으로 보이는 깜빡임을 막는다
  const showAnnualSummary = formType === 'attendance' && (approvalType === 'approval' ? !!rowState.caCd : initTableState !== null);

  const formTypeName = formType === 'attendance' ? t('근태') : formType === 'cdraft' ? t('기안') : t('업무보고');

  return (
    <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 max800' : 'oneColumn'}`}>
      <Root loading={false} className={size.innerSize.W > 1024 || viewTable ? 'showRoot' : 'hideRoot'}>
        <List
          formType={formType}
          approvalType={approvalType}
          tableState={tableState}
          setTableState={setTableState}
          initTableState={initTableState}
          setInitTableState={setInitTableState}
          setRowState={setRowState}
          isNewAdd={isNewAdd}
          setIsNewAdd={setIsNewAdd}
          addBtnAction={addBtnAction}
          formatInTuiForm={formatInTuiForm}
        />
      </Root>
      {(size.innerSize.W > 1024 || !viewTable) && (
        <Root loading={false}>
          <div className='inputFormsWrapper flexRowEm'>
            {size.innerSize.W <= 1024 && <BackButton func={onBackPrev} />}
            <div className='formTitle'>{isNewAdd ? t(`${formTypeName} 신청`) : t('신청 정보')}</div>
            {/**
             * 연차 현황은 근태 건이 아니라 신청자에게 속한 정보라서 본문(건 속성 목록)이 아닌 헤더에 둔다.
             * 결재 탭에서 보이는 값은 결재자 본인이 아니라 상신자의 연차다.
             */}
            {showAnnualSummary && (
              <div className='annualSummary'>
                <span className='annualItem'>
                  <span className='label'>{t('총 연차일수')}</span>
                  <span className='value'>{`${getAnnualDayString(annualSource.uAnnualnum)}${t('일')} (${getAnnualHourString(annualSource.uAnnualnum)}${t('시간')})`}</span>
                </span>
                <span className='annualItem'>
                  <span className='label'>{t('사용 완료')}</span>
                  <span className='value'>{`${getAnnualDayString(annualSource.uRannualnum)}${t('일')} (${getAnnualHourString(annualSource.uRannualnum)}${t('시간')})`}</span>
                </span>
                <span className='annualItem'>
                  <span className='label'>{t('결재 대기')}</span>
                  <span className='value'>{`${getAnnualDayString(annualSource.uPannualnum)}${t('일')} (${getAnnualHourString(annualSource.uPannualnum)}${t('시간')})`}</span>
                  <span className='note'>{`(${t('신청 중')})`}</span>
                </span>
                <span className='annualItem'>
                  <span className='label'>{t('신청 가능일수')}</span>
                  <span className={`value ${availableHours < 0 ? 'negative' : 'available'}`}>{`${hoursToDayString(availableHours)}${t('일')} (${availableHours}${t('시간')})`}</span>
                </span>
              </div>
            )}
          </div>
          <Form
            formType={formType}
            approvalType={approvalType}
            rowState={rowState}
            setRowState={setRowState}
            isNewAdd={isNewAdd}
            setIsNewAdd={setIsNewAdd}
            tableState={tableState}
            setInitTableState={setInitTableState}
            formatInTuiForm={formatInTuiForm}
          />
        </Root>
      )}
    </div>
  );
};

export default AttendanceForm;
